import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  theme,
  Text,
  Select,
  SelectOption,
  message,
  Spin,
  Tag,
  Input,
} from 'components';
import { startCase } from 'lodash';
import errorReporter from '@app/errorReporter';
import { useDebounce } from 'hooks';
import { PopulatedUser } from 'api/UserController/getProfile';
import { CachedOrg } from '@bb/automaton';
import type { SupraLockboxJsonType } from '@/app-package';
import { getSupraLockboxData } from 'api/SupraLockboxController';
import { featureEnabled } from 'utils/featureFlagBrowserClient';

type LockboxSearchPropsType = {
  mlsStaffOrgId?: string;
  user?: PopulatedUser & { org: CachedOrg };
  onValueChange: (lockboxes: string[]) => void;
  setOptions: React.Dispatch<React.SetStateAction<SupraLockboxJsonType[]>>;
  options: SupraLockboxJsonType[];
  values: string[];
  setValues: React.Dispatch<React.SetStateAction<string[]>>;
  lockboxSearchLoading: boolean;
  setLockboxSearchLoading: React.Dispatch<React.SetStateAction<boolean>>;
  lockboxSearchQuery: string;
  setLockboxSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  scannedLockboxesChanged?: boolean;
  scannedLockboxesChangedCallback?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  scannedLockboxes: SupraLockboxJsonType[];
};

const LOCKBOX_SERIAL_MIN = 4000000;
const LOCKBOX_SERIAL_MAX = 2147483647;

const LockboxSearch = ({
  mlsStaffOrgId,
  user,
  onValueChange,
  options,
  setOptions,
  values,
  setValues,
  lockboxSearchLoading,
  setLockboxSearchLoading,
  lockboxSearchQuery,
  setLockboxSearchQuery,
  scannedLockboxesChanged,
  scannedLockboxesChangedCallback,
  scannedLockboxes,
}: LockboxSearchPropsType) => {
  const debounceQuery = useDebounce(lockboxSearchQuery, 500);

  const [inputValue, setInputValue] = useState<string>('');

  const valuesRef = useRef<string[] | null>(null);

  // Automatically choose the 'selected' lockboxes when a matching lockbox is found from the search
  // Works for both single lockboxes and cartons
  useEffect(() => {
    if (scannedLockboxesChanged) {
      setOptions([]);
      setLockboxSearchQuery('');
      setInputValue('');
      valuesRef.current = null;
      scannedLockboxesChangedCallback?.(false);
    } else if (options.length && !lockboxSearchLoading) {
      /*
      Only consider changing the selected values of the lockbox search if there's lockbox options to choose from
      and we're no longer waiting for the lockboxes to be fetched from ListLockboxes()
      Make sure that the values have actually changed since the previous render to avoid a rerender loop then
      Change the selected values of the lockbox search
      */
      const prevValues = valuesRef.current;
      const selectedValues = options.map(option => option.serialNumber);
      const areValuesSame =
        prevValues &&
        prevValues.length === selectedValues.length &&
        prevValues.every((val, idx) => val === selectedValues[idx]);

      if (!areValuesSame) {
        valuesRef.current = selectedValues;
        setLockboxSearchQuery('');
        setInputValue('');
        onValueChange(selectedValues);
      }
    }
  }, [
    options,
    onValueChange,
    lockboxSearchLoading,
    scannedLockboxesChanged,
    setOptions,
    setLockboxSearchQuery,
    scannedLockboxesChangedCallback,
  ]);

  // When the user-inputted lockbox query has changed, begin searching for matching lockboxes
  // These lockbox results are now a list of 'options'
  // Multiple lockboxes may show up if a carton number was entered
  useEffect(() => {
    if (!debounceQuery) return;
    const getLockboxList = async () => {
      setLockboxSearchLoading(true);
      try {
        const { lockboxes } = await getSupraLockboxData({
          searchText: debounceQuery,
          cartonNumber: debounceQuery,
          mlsStaffOrgId,
          lockboxStatuses: ['New', 'Used', 'Active', 'Assigned'],
          requireOwnerAsMLS: false,
          filterBySupraOwnerMapping: true,
        });

        const scannedLockboxesSerialNumbers = scannedLockboxes.map(
          scannedLockbox => scannedLockbox.serialNumber,
        );
        if (
          lockboxes.length > 0 &&
          lockboxes.some(
            lockboxOption =>
              !scannedLockboxesSerialNumbers.includes(
                lockboxOption.serialNumber,
              ),
          )
        ) {
          setInputValue('');
          setOptions(lockboxes);
        }
      } catch (err) {
        message.error(err);
        errorReporter.error(err);
      } finally {
        setLockboxSearchLoading(false);
      }
    };

    getLockboxList();
  }, [
    debounceQuery,
    mlsStaffOrgId,
    setOptions,
    setLockboxSearchLoading,
    user?.org.id,
  ]);
  // [OB-541] This code can be removed when the improvedLockboxAssignmentFeature flag is retired
  const convertNewValues = (newValues: string[]) => {
    const cartonPrefix = 'carton-';
    const cartonNumber = newValues
      .find(value => value.startsWith(cartonPrefix))
      ?.split('-')[1];

    if (cartonNumber) {
      const serialNumbers = options
        .filter(lockbox => lockbox.cartonNumber === cartonNumber)
        .map(lockbox => lockbox.serialNumber);

      return newValues
        .filter(value => !value.startsWith(cartonPrefix))
        .concat(serialNumbers);
    }
    return newValues;
  };
  // [OB-541] This code can be removed when the improvedLockboxAssignmentFeature flag is retired
  const getOwnerOrAssigneeLabel = (
    type: string,
    mlsOwnerName?: string,
    branchOwnerName?: string,
    orgOwnerName?: string,
    officeOwnerName?: string,
  ) => {
    switch (type) {
      case 'branch':
        return branchOwnerName || 'Branch';
      case 'org':
        return orgOwnerName || 'Brokerage';
      case 'mls':
        return mlsOwnerName || 'MLS';
      case 'office':
        return officeOwnerName || 'Office';
      default:
        return startCase(type);
    }
  };
  // [OB-541] This code can be removed when the improvedLockboxAssignmentFeature flag is retired
  const createSelectOptions = () => {
    const cartonOptions = options.find(
      lockbox => lockbox.cartonNumber === lockboxSearchQuery,
    )
      ? [
          <SelectOption
            key={`carton-${lockboxSearchQuery}`}
            value={`carton-${lockboxSearchQuery}`}
            label={`${lockboxSearchQuery}`}
          >
            <div>
              {`Carton ${lockboxSearchQuery}`}
              <Text strong style={{ marginLeft: '10px' }}>
                Count
              </Text>
              <Text style={{ marginLeft: '5px' }}>
                {
                  options.filter(
                    lockbox => lockbox.cartonNumber === lockboxSearchQuery,
                  ).length
                }
              </Text>
            </div>
          </SelectOption>,
        ]
      : [];

    const lockboxOptions = options.map((lockbox: any) => (
        <SelectOption
          key={lockbox.serialNumber}
          value={lockbox.serialNumber}
          label={lockbox.serialNumber}
        >
          <div>
            {`Lockbox ${lockbox.serialNumber}`}
            <Text style={{ marginLeft: '5px' }}>
              {lockbox.firstName
                ? `${lockbox.firstName} ${lockbox.lastName}`
                : getOwnerOrAssigneeLabel(
                    lockbox.ownerType,
                    lockbox.mlsOwnerName,
                    lockbox.branchOwnerName,
                    lockbox.orgOwnerName,
                    lockbox.officeOwnerName,
                  )}
            </Text>
          </div>
        </SelectOption>
      ));

    const isIncludedQueryInOptions = options.some(lockbox => (
        lockbox.serialNumber === lockboxSearchQuery ||
        lockbox.cartonNumber === lockboxSearchQuery
      ));

    // Lockbox serial number must be within valid range
    const isValidQuery =
      Number.isInteger(Number(lockboxSearchQuery)) &&
      Number(lockboxSearchQuery) >= LOCKBOX_SERIAL_MIN &&
      Number(lockboxSearchQuery) <= LOCKBOX_SERIAL_MAX;

    const queryOptions = [];
    if (!isIncludedQueryInOptions && isValidQuery) {
      queryOptions.push(
        <SelectOption
          key={`${lockboxSearchQuery}`}
          value={`${lockboxSearchQuery}`}
          label={`${lockboxSearchQuery}`}
        >
          <div>{`Lockbox ${lockboxSearchQuery}`}</div>
        </SelectOption>,
      );
    }

    return [...cartonOptions, ...lockboxOptions, ...queryOptions];
  };

  return (
    <StyledSearchContainer>
      <StyledFlexContainer>
        {featureEnabled('improvedLockboxAssignmentFeature') ? (
          <StyledInput
            placeholder="Search for lockbox"
            value={inputValue}
            onChange={e => {
              setInputValue(e.target.value);
              if (e.target.value.length >= 8) {
                setLockboxSearchQuery(e.target.value);
              }
            }}
            data-cy="quick-assignment-lockbox-search"
          />
        ) : (
          <StyledSelect
            optionLabelProp="label"
            mode="multiple"
            placeholder="Search for lockbox"
            value={values}
            data-cy="quick-assignment-lockbox-search"
            notFoundContent={
              lockboxSearchLoading ? (
                <Spin size="small" />
              ) : (
                <StyledNoResultContainer>
                  {lockboxSearchQuery ? (
                    <Text type="secondary">No lockboxes found</Text>
                  ) : (
                    <Text type="secondary">
                      Search lockbox with serial number
                    </Text>
                  )}
                </StyledNoResultContainer>
              )
            }
            onChange={(newValues: string[]) => {
              const convertedValues = convertNewValues(newValues);
              onValueChange(convertedValues.sort());
            }}
            filterSort={(optionA, optionB) => {
              if (optionA.value?.toString().startsWith('carton-')) return -1;
              if (optionB.value?.toString().startsWith('carton-')) return 1;
              return 0;
            }}
            defaultValue={`carton-${lockboxSearchQuery}`}
            searchValue={lockboxSearchQuery}
            onSearch={searchQuery => {
              setLockboxSearchLoading(true);
              setLockboxSearchQuery(searchQuery);
            }}
          >
            {createSelectOptions()}
          </StyledSelect>
        )}
      </StyledFlexContainer>
    </StyledSearchContainer>
  );
};

export default LockboxSearch;

// [OB-541] This code can be removed when the improvedLockboxAssignmentFeature flag is retired
const StyledTag = styled(Tag)`
  && {
    color: ${theme.gray8};
    border: 1px solid ${theme.gray6};
    border-radius: 0px;
  }
`;

const StyledInput = styled(Input)`
  width: 100%;
  position: relative;
  border-radius: 2px;
  margin-bottom: 10px;
`;
// [OB-541] This code can be removed when the improvedLockboxAssignmentFeature flag is retired
const StyledSelect = styled(Select)`
  width: 100%;
  position: relative;
  border-radius: 2px;
  .ant-select-selection-placeholder {
    padding: 0 0 0 25px;
  }
  .ant-select-selection-overflow {
    margin: 0 0 0 25px;
  }
`;

const StyledFlexContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledSearchContainer = styled.div`
  margin-top: 10px;
  width: 40%;
`;
// [OB-541] This code can be removed when the improvedLockboxAssignmentFeature flag is retired
const StyledNoResultContainer = styled.div`
  text-align: center;
  margin: 20px 0px;
`;
